import { useMemo, useState } from 'react'

import {
  Box,
  Input,
  Select,
  Text,
  createListCollection,
  useFilter,
} from '@chakra-ui/react'

import { CheckIcon } from '#components/icons/check-icon.tsx'
import { GithubIcon } from '#components/icons/github-icon.tsx'

interface RepoItem {
  id: number
  full_name: string
  lastUsedAt?: number | string | null
}

interface RepositorySelectProps {
  repos: RepoItem[]
  value: string[]
  onValueChange: (details: { value: string[] }) => void
}

export function RepositorySelect({
  repos,
  value,
  onValueChange,
}: RepositorySelectProps) {
  const { contains } = useFilter({ sensitivity: 'base' })

  const collection = useMemo(() => {
    return createListCollection({
      items: repos,
      itemToString: (item) => item.full_name,
      itemToValue: (item) => item.id.toString(),
    })
  }, [repos])

  const [searchQuery, setSearchQuery] = useState('')

  const filteredItems = useMemo(() => {
    if (!searchQuery) return collection.items
    return collection.items.filter((item) =>
      contains(item.full_name, searchQuery),
    )
  }, [collection.items, searchQuery, contains])

  const { recentlyUsed, other } = useMemo(() => {
    const recent: typeof filteredItems = []
    const rest: typeof filteredItems = []
    for (const item of filteredItems) {
      if (item.lastUsedAt) {
        recent.push(item)
      } else {
        rest.push(item)
      }
    }
    // Sort recently used by lastUsedAt descending (most recent first)
    recent.sort(
      (a, b) =>
        new Date(b.lastUsedAt!).getTime() - new Date(a.lastUsedAt!).getTime(),
    )
    return { recentlyUsed: recent, other: rest }
  }, [filteredItems])

  const hasGroups = recentlyUsed.length > 0 && other.length > 0

  const triggerLabel = useMemo(() => {
    if (value.length === 0) return null
    if (value.length === 1) {
      const selected = collection.items.find(
        (item) => item.id.toString() === value[0],
      )
      if (!selected) return null
      // Repo name only (without the owner) to keep the trigger compact.
      return selected.full_name.split('/')[1] ?? selected.full_name
    }
    return `${value.length} repositories selected`
  }, [value, collection.items])

  const renderItem = (item: RepoItem) => {
    const checked = value.includes(item.id.toString())
    return (
      <Select.Item
        key={item.id}
        item={item}
        gap="2"
        justifyContent="flex-start"
      >
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          flexShrink="0"
          boxSize="4"
          borderWidth="1px"
          borderRadius="sm"
          borderColor={checked ? 'colorPalette.solid' : 'border'}
          bg={checked ? 'colorPalette.solid' : 'transparent'}
          color={checked ? 'colorPalette.contrast' : 'transparent'}
        >
          {checked ? <CheckIcon size="0.75em" /> : null}
        </Box>
        {item.full_name}
      </Select.Item>
    )
  }

  return (
    <Select.Root
      multiple
      collection={collection}
      value={value}
      onValueChange={onValueChange}
      size="sm"
      variant="subtle"
      positioning={{ sameWidth: false }}
      width="auto"
      closeOnSelect={false}
    >
      <Select.HiddenSelect />
      <Select.Control>
        <Select.Trigger
          justifyContent="flex-start"
          gap="2"
          overflow="hidden"
          borderRadius="full"
          bg="transparent"
          _hover={{ bg: 'bg.subtle', borderColor: 'transparent' }}
        >
          <Box flexShrink="0" display="inline-flex">
            <GithubIcon />
          </Box>
          {triggerLabel ? (
            <Text truncate>{triggerLabel}</Text>
          ) : (
            <Text color="fg.muted" whiteSpace="nowrap">
              Select repositories
            </Text>
          )}
        </Select.Trigger>
      </Select.Control>
      <Select.Positioner>
        <Select.Content
          flexDirection="column"
          minH="0"
          maxH="calc(var(--available-height) - 4rem)"
          maxW="300px"
          minW="200px"
        >
          <Select.ItemGroup>
            <Input
              name="repository"
              placeholder="Search repositories"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="sm"
            />
          </Select.ItemGroup>
          {hasGroups ? (
            <>
              <Select.ItemGroup overflowY="auto" flex="1">
                <Select.ItemGroupLabel>Recently used</Select.ItemGroupLabel>
                {recentlyUsed.map(renderItem)}
              </Select.ItemGroup>
              <Select.ItemGroup overflowY="auto" flex="1">
                <Select.ItemGroupLabel>All repositories</Select.ItemGroupLabel>
                {other.map(renderItem)}
              </Select.ItemGroup>
            </>
          ) : (
            <Select.ItemGroup overflowY="auto" flex="1">
              {recentlyUsed.length > 0
                ? recentlyUsed.map(renderItem)
                : other.map(renderItem)}
            </Select.ItemGroup>
          )}
        </Select.Content>
      </Select.Positioner>
    </Select.Root>
  )
}
